import React, { FC, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";

import { Link } from "@natera/navigation";
import { routes } from "@app/routing";
import { PaymentPlanSummaryCard } from "@app/postBill/components/paymentPlanSummaryCard";
import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import { PaymentCardDisplay } from "@app/postBill/components/paymentCardDisplay";
import { PaymentSchedule } from "@app/postBill/components/paymentSchedule";
import { transformInstallments } from "@app/postBill/utils/transformInstallments";
import { PostBillAPIContext, PostBillContext } from "@app/provider";
import { PaymentScheduleItem } from "@app/postBill/components/paymentSchedule/paymentSchedule";
import {
  BillingPaymentPlanStatus,
  PaymentPlanDetailItem,
} from "@app/postBill/models";
import { SpinnerView, Notification } from "@app/components";
import { BlueHeaderContent } from "@app/components/ui/layout";
import { usePostBillHeapEvent } from "@app/postBill/hooks";
import { HEAP_EVENTS } from "@app/provider/types";
import { logMsg } from "@app/utils/logMsg";

import "./billingPaymentPlanDetail.scss";

const messages = defineMessages({
  billingPaymentPlanDetailTitle: {
    id: "billingPaymentPlanDetailTitle",
    defaultMessage: "Payment plan details",
  },
  billingPaymentPlanDetailPayFullBalance: {
    id: "billingPaymentPlanDetailPayFullBalance",
    defaultMessage: "Pay full balance",
  },
  billingPaymentPlanDetailPaymentMethod: {
    id: "billingPaymentPlanDetailPaymentMethod",
    defaultMessage: "Payment method",
  },
  billingPaymentPlanDetailPayTotalPayments: {
    id: "billingPaymentPlanDetailPayTotalPayments",
    defaultMessage: "{paymentPlanCount} total payments",
  },
  billingPaymentPlanDetailStatusErrorContent: {
    id: "billingPaymentPlanDetailStatusErrorContent",
    defaultMessage:
      "There is an issue with your payment method.{br} Please contact your payment provider for more details or update your payment method.",
  },
  billingPaymentPlanDetailStatusErrorTitle: {
    id: "billingPaymentPlanDetailStatusErrorTitle",
    defaultMessage: "Update your payment method",
  },
  billingPaymentPlanDetailStatusUnpaidCancelledPlan: {
    id: "billingPaymentPlanDetailStatusUnpaidCancelledPlan",
    defaultMessage:
      "This payment plan has been canceled on {cancellationDate}. Please pay the remaining balance.",
  },
  billingPaymentPlanDetailsReviewNav: {
    id: "billingPaymentPlanDetailsReviewNav",
    defaultMessage: "Review and pay",
  },
  billingPaymentPlanDetailStatusPaidCancelledContent: {
    id: "billingPaymentPlanDetailStatusPaidCancelledContent",
    defaultMessage:
      "This payment plan was canceled on {cancellationDate}, but we’ve received all remaining balance.",
  },
  billingPaymentPlanDetailStatusCompletedContent: {
    id: "billingPaymentPlanDetailStatusCompletedContent",
    defaultMessage: "Thank you for making all the payments!",
  },
});

const BillingPaymentPlanDetail: FC = () => {
  const intl = useIntl();
  const { getPaymentPlanDetail, loadingStates } =
    useContext(PostBillAPIContext);
  const [payments, setPayments] = useState<PaymentScheduleItem[]>([]);
  const [paymentDetail, setPaymentDetail] = useState<PaymentPlanDetailItem>();
  const { pageFrom } = useContext(PostBillContext);
  const { trackPostBillHeapEvent } = usePostBillHeapEvent();

  const navigate = useNavigate();

  const location = useLocation();
  const { paymentPlanId } = location.state ?? {};

  const getPaymentPlanDetails = async (paymentPlanId: string) => {
    try {
      const getPaymentPlanDetails = await getPaymentPlanDetail({
        paymentPlanId,
      });
      if (getPaymentPlanDetails.data) {
        setPaymentDetail(getPaymentPlanDetails.data);
        setPayments(transformInstallments(getPaymentPlanDetails.data, intl));
      }
    } catch {
      logMsg(
        "error",
        "postbill.payment-plan",
        "Failed to get Payment Plan Details"
      );
    }
  };

  const canShowPayButton = () => {
    return (
      paymentDetail?.remainingBalance &&
      (paymentDetail?.status === BillingPaymentPlanStatus.ACTIVE ||
        paymentDetail?.status === BillingPaymentPlanStatus.ERROR)
    );
  };

  const handleBack = () => navigate(pageFrom || routes.billingCenter);

  const handlePayFullBalance = () => {
    trackPostBillHeapEvent({
      eventKey: HEAP_EVENTS.upp_click_billing_payment_plan_pay_full_balance,
    });
    navigate(routes.billingPayment, {
      state: {
        paymentPlanId,
        payFullBalance: true,
        from: routes.billingPaymentPlanDetail,
      },
    });
  };

  const handleClickEditPaymentMethod = async () => {
    trackPostBillHeapEvent({
      eventKey: HEAP_EVENTS.upp_click_billing_payment_plan_edit_payment_method,
    });
    navigate(routes.billingUpdatePayment, {
      state: {
        paymentPlanId: paymentPlanId,
      },
    });
  };

  const renderNotification = () => {
    if (paymentDetail?.status === BillingPaymentPlanStatus.ERROR) {
      return (
        <Notification
          className="billing-payment-plan-detail__notification"
          type="error"
        >
          <h3 className="billing-payment-plan-detail__notification__title">
            {intl.formatMessage(
              messages.billingPaymentPlanDetailStatusErrorTitle
            )}
          </h3>
          <p className="billing-payment-plan-detail__notification__content">
            {intl.formatMessage(
              messages.billingPaymentPlanDetailStatusErrorContent,
              {
                br: <br />,
              }
            )}
          </p>
        </Notification>
      );
    }

    if (
      paymentDetail?.status === BillingPaymentPlanStatus.CANCELLED &&
      paymentDetail?.remainingBalance > 0
    ) {
      return (
        <Notification
          className="billing-payment-plan-detail__notification"
          type="error"
        >
          <p className="billing-payment-plan-detail__notification__content">
            {intl.formatMessage(
              messages.billingPaymentPlanDetailStatusUnpaidCancelledPlan,
              {
                cancellationDate: intl.formatDate(
                  paymentDetail.cancellationDate,
                  {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }
                ),
              }
            )}
          </p>
          <Link
            rel="noreferrer"
            to={routes.reviewBills}
            state={{
              from: routes.billingPaymentPlanDetail,
              paymentPlanId: paymentPlanId,
            }}
          >
            {intl.formatMessage(messages.billingPaymentPlanDetailsReviewNav)}
          </Link>
        </Notification>
      );
    }

    if (
      paymentDetail?.status === BillingPaymentPlanStatus.CANCELLED &&
      !paymentDetail?.remainingBalance
    ) {
      return (
        <Notification
          className="billing-payment-plan-detail__notification"
          type="info"
        >
          <p className="billing-payment-plan-detail__notification__content">
            {intl.formatMessage(
              messages.billingPaymentPlanDetailStatusPaidCancelledContent,
              {
                cancellationDate: intl.formatDate(
                  paymentDetail.cancellationDate,
                  {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }
                ),
              }
            )}
          </p>
        </Notification>
      );
    }

    if (paymentDetail?.status === BillingPaymentPlanStatus.COMPLETED) {
      return (
        <Notification
          className="billing-payment-plan-detail__notification"
          type="success"
        >
          <p className="billing-payment-plan-detail__notification__content">
            {intl.formatMessage(
              messages.billingPaymentPlanDetailStatusCompletedContent
            )}
          </p>
        </Notification>
      );
    }
  };

  const renderPaymentCard = (paymentDetail: PaymentPlanDetailItem) => {
    if (
      paymentDetail?.status === BillingPaymentPlanStatus.ACTIVE ||
      paymentDetail?.status === BillingPaymentPlanStatus.ERROR
    ) {
      return (
        <PaymentCardDisplay
          brand={paymentDetail?.paymentMethodId?.brand}
          lastFour={paymentDetail?.paymentMethodId?.last4digits}
          expiryDate={paymentDetail?.paymentMethodId?.expiry}
          onEditClick={handleClickEditPaymentMethod}
        />
      );
    } else {
      return (
        <PaymentCardDisplay
          brand={paymentDetail?.paymentMethodId?.brand}
          lastFour={paymentDetail?.paymentMethodId?.last4digits}
          expiryDate={paymentDetail?.paymentMethodId?.expiry}
        />
      );
    }
  };

  useEffect(() => {
    if (!paymentPlanId) {
      navigate(routes.billingCenter);
    } else {
      getPaymentPlanDetails(paymentPlanId);
    }
  }, [paymentPlanId]);

  return (
    <>
      <SpinnerView isLoading={loadingStates.getPaymentDetails} />
      <BlueHeaderContent
        blueHeaderTitle={intl.formatMessage(
          messages.billingPaymentPlanDetailTitle
        )}
        backArrowHeaderButton={handleBack}
        noGradient
      >
        <div className="billing-payment-plan-detail">
          {renderNotification()}
          {paymentDetail && (
            <div className="billing-payment-plan-detail__content">
              <PaymentPlanSummaryCard
                paid={paymentDetail.paidToDate}
                remaining={paymentDetail.remainingBalance}
                total={paymentDetail.orderTotal}
                currency={paymentDetail.currency}
                testName={paymentDetail.testName}
                planStartDate={paymentDetail.planStartDate}
                paymentDate={paymentDetail.paymentDate}
              />

              {canShowPayButton() && (
                <ButtonNoShadow
                  raised
                  onClick={handlePayFullBalance}
                  className="billing-payment-plan-detail__pay-full-balance-button"
                  data-testid="billingPayFullBalance"
                >
                  {intl.formatMessage(
                    messages.billingPaymentPlanDetailPayFullBalance
                  )}
                </ButtonNoShadow>
              )}
              {paymentDetail?.paymentMethodId && (
                <>
                  <div className="billing-payment-plan-detail__subtitle">
                    {intl.formatMessage(
                      messages.billingPaymentPlanDetailPaymentMethod
                    )}
                  </div>
                  {renderPaymentCard(paymentDetail)}
                </>
              )}

              <PaymentSchedule payments={payments} />
            </div>
          )}
        </div>
      </BlueHeaderContent>
    </>
  );
};

export default BillingPaymentPlanDetail;
